import { create } from 'zustand';
import { z } from 'zod';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { WildcardAdminPage } from '@/graphql/generated/graphql';

// Configure dayjs plugins
dayjs.extend(isSameOrAfter);

// Maximum file size in bytes (200KB)
const MAX_FILE_SIZE = 200 * 1024;
// Maximum dimensions for the logo
const MAX_WIDTH = 280;
const MAX_HEIGHT = 40;

const getValidationMessage = (page: WildcardAdminPage | null, key: string, fallback: string) => {
  return page?.form?.validations?.[key as keyof typeof page.form.validations] || fallback;
};

// Function to validate image dimensions
const validateImageDimensions = (file: File): Promise<boolean> => {
  return new Promise((resolve) => {
    if (!(file instanceof File)) {
      resolve(false);
      return;
    }

    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      resolve(img.width <= MAX_WIDTH && img.height <= MAX_HEIGHT);
    };
    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      resolve(false);
    };
    img.src = URL.createObjectURL(file);
  });
};

export const createAnnouncementSchema = (page: WildcardAdminPage | null) =>
  z
    .object({
      startDate: z.date().refine((date) => dayjs(date).startOf('day').isSameOrAfter(dayjs().startOf('day')), {
        message: getValidationMessage(page, 'startDateFuture', 'Start date must be today or later'),
      }),
      endDate: z.date().refine((date) => dayjs(date).isValid(), {
        message: getValidationMessage(page, 'endDateValid', 'End date must be valid'),
      }),
      description: z
        .string()
        .min(1, { message: getValidationMessage(page, 'descriptionRequired', 'Description is required') })
        .max(300, {
          message: getValidationMessage(page, 'descriptionMaxLength', 'Description cannot exceed 300 characters'),
        }),
      schoolIds: z
        .array(z.string())
        .min(1, { message: getValidationMessage(page, 'schoolRequired', 'At least one school must be selected') }),
      logo: z
        .union([z.instanceof(File), z.string()])
        .refine(
          (val) => {
            if (val instanceof File) {
              return val.size > 0 || val.name !== '';
            }
            return false;
          },
          { message: getValidationMessage(page, 'logoRequired', 'Logo is required') },
        )
        .refine(
          (val) => {
            if (val instanceof File) {
              return val.size <= MAX_FILE_SIZE || val.size === 0;
            }
            return true;
          },
          { message: getValidationMessage(page, 'logoSize', 'Logo file size must not exceed 200KB') },
        )
        .superRefine(async (val, ctx) => {
          if (val instanceof File) {
            if (val.size === 0) {
              return true;
            }
            const validDimensions = await validateImageDimensions(val);
            if (!validDimensions) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: getValidationMessage(page, 'logoDimensions', 'Logo dimensions must not exceed 280x40 pixels'),
              });
            }
          }
        }),
      logoId: z.string().optional(),
    })
    .refine(
      (data) => {
        return dayjs(data.endDate).isSameOrAfter(dayjs(data.startDate));
      },
      {
        message: getValidationMessage(page, 'endDateAfterStart', 'End date must be after or equal to start date'),
        path: ['endDate'],
      },
    )
    .refine(
      (data) => {
        const diffInDays = dayjs(data.endDate).diff(dayjs(data.startDate), 'day');
        return diffInDays <= 3;
      },
      {
        message: getValidationMessage(page, 'dateRangeLimit', 'Date range cannot exceed 3 days'),
        path: ['endDate'],
      },
    )
    .refine(
      (data) => {
        return dayjs(data.endDate).isSameOrAfter(dayjs(data.startDate));
      },
      {
        message: getValidationMessage(page, 'endDateAfterStart', 'End date must be after or equal to start date'),
        path: ['endDate'],
      },
    );

export type AnnouncementFormValues = z.infer<ReturnType<typeof createAnnouncementSchema>>;

interface AnnouncementState {
  formData: AnnouncementFormValues;
  editingId: string | null;
  page: WildcardAdminPage | null;
  resetForm: () => void;
  setEditingAnnouncement: (id: string | null, data: AnnouncementFormValues) => void;
  setPage: (page: WildcardAdminPage | null) => void;
}

const initialState: AnnouncementFormValues = {
  startDate: dayjs().startOf('day').toDate(),
  endDate: dayjs().add(3, 'day').endOf('day').toDate(),
  description: '',
  logo: '' as string, // Initialize with empty string to satisfy type
  logoId: undefined,
  schoolIds: [],
};

export const useAnnouncementStore = create<AnnouncementState>((set) => ({
  formData: initialState,
  editingId: null,
  page: null,
  setEditingAnnouncement: (id, data) =>
    set({
      editingId: id,
      formData: data,
    }),
  setPage: (page) => set({ page }),
  resetForm: () => set({ formData: initialState, editingId: null }),
}));
